import { useEffect, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { verifyEmail, resendVerificationEmail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import Logo from "../components/Logo";
import { CheckCircle2, MailCheck, AlertCircle, ArrowRight, RefreshCw } from "lucide-react";

const RESEND_COOLDOWN_SECONDS = 60;

export default function VerifyEmail() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [status, setStatus] = useState("idle"); // idle | verifying | success | error | resend
  const [message, setMessage] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resending, setResending] = useState(false);
  const ranOnce = useRef(false);

  useEffect(() => {
    if (ranOnce.current) return;
    ranOnce.current = true;

    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");

    if (token) {
      setStatus("verifying");
      verifyEmail(token)
        .then(async () => {
          setStatus("success");
          setMessage("Your email has been verified.");
          try { await refreshUser(); } catch { /* ignore */ }
        })
        .catch((err) => {
          setStatus("error");
          setMessage(err?.response?.data?.detail || "This verification link is invalid or has expired.");
        });
    } else {
      setStatus("resend");
    }
  }, [refreshUser]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const handleResend = async () => {
    if (resending || resendCooldown > 0) return;
    setResending(true);
    try {
      await resendVerificationEmail();
      setMessage(`Verification email sent to ${user?.email || "your inbox"}. Check spam if you don't see it.`);
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Failed to resend. Please try again.";
      setMessage(detail);
      // If 429, parse seconds from message and start cooldown
      const match = String(detail).match(/(\d+)\s*seconds?/);
      if (match) setResendCooldown(parseInt(match[1], 10));
    }
    setResending(false);
  };

  const goNext = () => {
    if (!user) return navigate("/auth", { replace: true });
    if (!user.role) return navigate("/select-role", { replace: true });
    if (user.role === "landlord") return navigate("/landlord-dashboard", { replace: true });
    if (user.role === "admin") return navigate("/admin", { replace: true });
    return navigate("/dashboard", { replace: true });
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: "#faf8f3" }}>
      <nav className="px-5 md:px-12 py-4 flex items-center justify-between border-b border-black/[0.06]">
        <Logo />
        <Link to="/browse" className="text-sm font-medium text-[#0d0d0d] hover:text-[#1a6b3c]" data-testid="verify-nav-browse">
          Browse
        </Link>
      </nav>

      <div className="flex-1 flex items-center justify-center px-5 py-12">
        <div className="w-full max-w-md text-center" data-testid="verify-email-page">
          {status === "verifying" && (
            <div data-testid="verify-status-verifying">
              <div className="w-10 h-10 border-4 border-[#1a6b3c] border-t-transparent rounded-full animate-spin mx-auto mb-6" />
              <h1 className="text-2xl font-black text-[#0d0d0d] font-['Fraunces'] mb-2">
                Verifying your email…
              </h1>
              <p className="text-[#888]">Hang tight, this only takes a second.</p>
            </div>
          )}

          {status === "success" && (
            <div data-testid="verify-status-success">
              <div className="w-16 h-16 rounded-full bg-[#1a6b3c]/[0.1] flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-9 h-9 text-[#1a6b3c]" />
              </div>
              <h1 className="text-3xl sm:text-4xl font-black text-[#0d0d0d] font-['Fraunces'] mb-3">
                You're verified!
              </h1>
              <p className="text-[#666] mb-8">
                {message} You now have full access to listings, messaging, and payments.
              </p>
              <Button
                data-testid="verify-continue-btn"
                onClick={goNext}
                className="w-full h-12 rounded-xl bg-[#1a6b3c] hover:bg-[#15562f] text-white font-semibold text-sm"
              >
                Continue <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {status === "error" && (
            <div data-testid="verify-status-error">
              <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="w-9 h-9 text-red-600" />
              </div>
              <h1 className="text-3xl sm:text-4xl font-black text-[#0d0d0d] font-['Fraunces'] mb-3">
                Link issue
              </h1>
              <p className="text-[#666] mb-8" data-testid="verify-error-message">{message}</p>
              {user ? (
                <Button
                  data-testid="verify-resend-btn"
                  onClick={handleResend}
                  disabled={resending || resendCooldown > 0}
                  className="w-full h-12 rounded-xl bg-[#1a6b3c] hover:bg-[#15562f] text-white font-semibold text-sm"
                >
                  {resending ? "Sending…" : resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend verification email"}
                  {!resending && resendCooldown === 0 && <RefreshCw className="w-4 h-4 ml-2" />}
                </Button>
              ) : (
                <Link to="/auth">
                  <Button className="w-full h-12 rounded-xl bg-[#1a6b3c] hover:bg-[#15562f] text-white font-semibold text-sm">
                    Sign in to resend
                  </Button>
                </Link>
              )}
            </div>
          )}

          {status === "resend" && (
            <div data-testid="verify-status-resend">
              <div className="w-16 h-16 rounded-full bg-[#1a6b3c]/[0.1] flex items-center justify-center mx-auto mb-6">
                <MailCheck className="w-9 h-9 text-[#1a6b3c]" />
              </div>
              <h1 className="text-3xl sm:text-4xl font-black text-[#0d0d0d] font-['Fraunces'] mb-3">
                Check your inbox
              </h1>
              <p className="text-[#666] mb-2">
                We sent a verification link to{" "}
                <span className="font-semibold text-[#0d0d0d]">{user?.email || "your email"}</span>.
              </p>
              <p className="text-[#888] text-sm mb-8">
                Click the link in the email to activate your account. The link expires in 24 hours.
              </p>

              {message && (
                <p className="text-sm text-[#1a6b3c] bg-[#1a6b3c]/[0.08] rounded-lg px-3 py-2 mb-4" data-testid="verify-message">
                  {message}
                </p>
              )}

              {user && (
                <Button
                  data-testid="verify-resend-btn"
                  onClick={handleResend}
                  disabled={resending || resendCooldown > 0}
                  variant="outline"
                  className="w-full h-12 rounded-xl border-[#1a6b3c] text-[#1a6b3c] hover:bg-[#1a6b3c]/[0.05] font-semibold text-sm"
                >
                  {resending ? "Sending…" : resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend verification email"}
                </Button>
              )}

              {user && !user.role && (
                <button
                  data-testid="verify-skip-btn"
                  onClick={() => navigate("/select-role", { replace: true })}
                  className="block w-full text-center text-xs text-[#888] hover:text-[#1a6b3c] mt-4 underline"
                >
                  Continue setting up your profile (you can verify later)
                </button>
              )}

              <p className="text-xs text-[#aaa] mt-6">
                Wrong email? <Link to="/contact" className="text-[#1a6b3c] hover:underline">Contact support</Link>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
